import CallIcon from "@mui/icons-material/Call";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { Fab, Stack, styled } from "@mui/material";
import { memo } from "react";

import { ORDER_TYPE_ENUM } from "@/constants/enums";
import { PAGE_PADDING } from "@/constants/layout";
import { Z_INDEX } from "@/constants/zIndex";
import { WHATSAPP_LINK_BASE } from "@/lib/config";
import { useAppSelector } from "@/store";
import { trackEvent } from "@/utils/analytics";
import { openLink } from "@/utils/webview";

/**
 * `rN` — the floating WhatsApp + Call pair, and the ONLY place in the whole
 * original bundle that contains a `wa.me` link.
 *
 * It is the LAST child of the menu body, a sibling of the scroll port rather
 * than something inside it, so it floats over the catalog and the cart alike:
 *
 * ```js
 * <iW>
 *   <iL ref={scrollRef}> …navbar, tabs, rails, catalog, footer, order bar… </iL>
 *   <rN/>
 * </iW>
 * ```
 *
 * ## The gate is the tenant's own data
 *
 * ```js
 * if (!t || 0 === t.length || r || a) return null;
 * ```
 *
 * `t` is `settings.public_phone_numbers`, `r` is `auth.isStaff` and `a` is
 * "this basket is dine-in". A tenant with no published number shows nothing —
 * which is why `butt-karahi-main`, whose `public_phone_numbers` is `[]`,
 * correctly renders neither button. A member of staff never sees them either
 * (they are the customer's route to the restaurant, and staff ARE the
 * restaurant), and neither does a customer sitting at a table.
 *
 * ## The number is normalised before either link is built
 *
 * ```js
 * l = t[0].replace(/ /g, "").replace(country_code ?? "", "").replace(/^0/, "");
 * l = "".concat(country_code ?? "").concat(l);
 * ```
 *
 * Spaces out, any leading country code stripped so it cannot be doubled, a
 * leading national `0` dropped, then the country code put back on the front.
 * `+923001234567`, `0300 1234567` and `3001234567` all normalise to the same
 * string for a `+92` tenant. Both buttons use that one value — `wa.me/<n>` and
 * `tel:<n>` — so they can never disagree.
 *
 * ## Two faithful oddities, deliberately kept
 *
 * 1. **Both Fabs carry `aria-label="Call"`.** The WhatsApp one included
 *    (`rz`'s own attribute). It is the original's copy.
 * 2. **`memo(…, () => !0)`** — the comparator returns true unconditionally, so
 *    this never re-renders after its first paint. The same trick `rw` uses on
 *    the recent-orders rail.
 */

/** `rF` — the fixed container. `l.ig` is `PAGE_PADDING`, i.e. 15. */
const ContactDock = styled(Stack)(({ theme }) => ({
  position: "fixed",
  zIndex: Z_INDEX.MAX,
  left: PAGE_PADDING,
  bottom: PAGE_PADDING,
  width: "fit-content",
  [theme.breakpoints.down("lg")]: {
    right: PAGE_PADDING,
    bottom: 4 * PAGE_PADDING + 5,
  },
}));

/** `rA` / `d` — both Fabs are the same styled `Fab`. */
const ContactFab = styled(Fab)({ zIndex: Z_INDEX.NONE });

/**
 * `rz` — the WhatsApp half. `color="success"`, and it opens `wa.me` through
 * `openLink` rather than an anchor so the RN shell can take it over.
 */
function WhatsAppFab({ number }: { number: string }) {
  return (
    <ContactFab
      aria-label="Call"
      color="success"
      size="medium"
      onClick={() => {
        openLink(`${WHATSAPP_LINK_BASE}/${number}`);
        trackEvent("Contact");
      }}
    >
      <WhatsAppIcon fontSize="large" />
    </ContactFab>
  );
}

/**
 * `K`, module `16329` — the Call half. It is a shared export in the original
 * (it takes its `color` from the caller), so it is a separate component here
 * too even though `rN` is its only call site.
 */
export function CallFab({
  number,
  color,
}: {
  number: string;
  color: "primary" | "success";
}) {
  return (
    <ContactFab
      color={color}
      aria-label="Call"
      size="medium"
      onClick={() => {
        openLink(`tel:${number}`);
        trackEvent("Contact");
      }}
    >
      <CallIcon />
    </ContactFab>
  );
}

function ContactFabs() {
  const { public_phone_numbers: phoneNumbers, country_code: countryCode } =
    useAppSelector((state) => state.settings.data);
  const isStaff = useAppSelector((state) => state.auth.isStaff);
  const orderType = useAppSelector((state) => state.cart.orderType);
  const isDineIn = orderType === ORDER_TYPE_ENUM.DINE_IN;

  if (!phoneNumbers || phoneNumbers.length === 0 || isStaff || isDineIn) {
    return null;
  }

  const code = countryCode ?? "";
  const national = (phoneNumbers[0] ?? "")
    .replace(/ /g, "")
    .replace(code, "")
    .replace(/^0/, "");
  const number = `${code}${national}`;

  return (
    <ContactDock spacing={1.5}>
      <WhatsAppFab number={number} />
      <CallFab number={number} color="primary" />
    </ContactDock>
  );
}

export default memo(ContactFabs, () => true);
